import React, { useEffect, useState } from 'react';
import { WithTranslationProps } from "Common/Components/Translation/WithTranslationProps";
import withTranslation from "Common/withTranslation";
import { ProductRequestProps } from './ProductRequestProps';
import { ProductRequestViewModel } from 'Common/ViewModels/ProductRequestViewModel';
import { Send, SendHorizonal, X } from 'lucide-react';
import Subtitle from 'Common/Components/Titles/Subtitle';
import { ProductRequestErrorsViewModel } from 'Common/ViewModels/Errors/ProductRequestErrorsViewModel';
import { axios_helper_async } from 'helpers/Axios';
import withNotifications, { WithNotificationsProps } from 'Common/withNotifications';
import { NotificationType } from 'Common/Components/Notifications/notificationTypes';
import LoadingButton from 'Common/Components/LoadingOverlay/LoadingButton/LoadingButton';

const modelFields: Record<keyof ProductRequestViewModel, string> = {
    comments: 'comments',
    email: 'email',
    name: 'name',
    phone: 'phone',
    quantity: 'quantity',
    product: 'product',
}

const initialErrors: ProductRequestErrorsViewModel = {
    email: '',
    name: '',
    quantity: ''
}

const ProductRequest: React.FC<WithNotificationsProps & WithTranslationProps & ProductRequestProps> = ({ 
    notifications,
    t,
    product,
    Close
}) => {
    const initialModel: ProductRequestViewModel = {
        comments: '',
        email: '',
        name: '',
        phone: '',
        quantity: 0,
        product: product
    }

    const [model, setModel] = useState<ProductRequestViewModel>(initialModel)
    const [errors, setErrors] = useState<ProductRequestErrorsViewModel>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }))
    }

    const validForm = () => {
        let errors: ProductRequestErrorsViewModel = {};
        if(!model.name) errors.name = "Product.ProductRequest.Validations.Name";
        if(!model.email) errors.email = "Product.ProductRequest.Validations.Email";
        if(!model.quantity) errors.quantity = "Product.ProductRequest.Validations.Quantity";

        setErrors(errors);

        return Object.keys(errors).length === 0;
    }

    const handleRequest = async () => {
        try{
            setIsLoading(true);
            if(validForm()) {                
                let response = await axios_helper_async<string>('/SOI/Send-Product-Request', 'POST', model);
            
                notifications.setNotifications(response.notifications);

                if(!response.isSuccess){
                    notifications.showNotification(t('AxiosAPI.InternalError'), NotificationType.Error, "Error");
                }
                else{
                    notifications.showNotification("Product.ProductRequest.Alerts.EmailSent", NotificationType.Success);
                    setModel(initialModel);
                    setErrors({});
                }
    
                if(response.isSuccess) Close();
            }
        }
        catch (err: any) {
            notifications.showNotification(err, NotificationType.Error);
        }
        finally {
            setIsLoading(false);
        }        
    }

    return (
        <React.Fragment>
            <div className="flex justify-center items-center overflow-hidden fixed inset-0 z-[48] p-2 min-h-screen">
                <div className="my-8 mx-auto max-w-sm sm:max-w-md md:max-w-lg z-[47] bg-white rounded-lg shadow-lg overflow-hidden w-full">
                    <div className="flex flex-col items-center p-6 text-center w-full gap-2">
                        <div className="flex flex-row justify-center items-center w-full">
                            <div className="flex justify-center items-center w-full">
                                {/* <h3 className="text-md font-bold text-black w-full">{t("Product.ProductRequest.Title")}</h3> */}
                                <Subtitle text={t("Product.ProductRequest.Title")}></Subtitle>
                            </div>
                            <div className="flex justify-center items-center">
                                <button onClick={Close}><X></X></button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 justify-center items-center w-full gap-2">
                            <div className="flex flex-col justify-center items-center w-full gap-2">
                                <label className="form-label">{t("Product.ProductRequest.Name")}<span className="text-red-500">*</span></label>
                                <input
                                    className={`form-input ${errors?.name ? "border-red-500" : null}`}
                                    id={modelFields.name}
                                    name={modelFields.name}
                                    value={model.name}
                                    onChange={handleChange}
                                    placeholder={t("Product.ProductRequest.Name")}
                                />
                                {errors && errors.name && <div className="mt-1 text-sm text-red-500">{t(errors.name)}</div>}
                            </div>
                            <div className="flex flex-col justify-center items-center w-full gap-2">
                                <label className="form-label">{t("Product.ProductRequest.Email")}<span className="text-red-500">*</span></label>
                                <input
                                    className={`form-input ${errors?.email ? "border-red-500" : null}`}
                                    id={modelFields.email}
                                    name={modelFields.email}
                                    value={model.email}
                                    onChange={handleChange}
                                    placeholder={t("Product.ProductRequest.EmailPlaceholder")}
                                />
                                {errors && errors.email && <div className="mt-1 text-sm text-red-500">{t(errors.email)}</div>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 justify-center items-center w-full gap-2">
                            <div className="flex flex-col justify-center items-center w-full gap-2">
                                <label className="form-label">{t("Product.ProductRequest.Phone")}</label>
                                <input
                                    className="form-input"
                                    id={modelFields.phone}
                                    name={modelFields.phone}
                                    value={model.phone}
                                    onChange={handleChange}
                                    placeholder={t("Product.ProductRequest.PhonePlaceholder")}
                                />
                            </div>
                            <div className="flex flex-col justify-center items-center w-full gap-2">
                                <label className="form-label">{t("Product.ProductRequest.Quantity")}<span className="text-red-500">*</span></label>
                                <input
                                    className={`form-input text-center ${errors?.quantity ? "border-red-500" : null}`}
                                    type='number'
                                    min={1}
                                    step={1}
                                    id={modelFields.quantity}
                                    name={modelFields.quantity}
                                    value={model.quantity}
                                    onChange={handleChange}
                                    placeholder={t("Product.ProductRequest.Quantity")}
                                />
                                {errors && errors.quantity && <div className="mt-1 text-sm text-red-500">{t(errors.quantity)}</div>}
                            </div>
                        </div>
                        <div className="flex flex-col justify-center items-center w-full gap-2">
                            <label className="form-label required">{t("Product.ProductRequest.Comments")}</label>
                            <textarea
                                className="form-input w-full"
                                id={modelFields.comments}
                                name={modelFields.comments}
                                rows={5}
                                value={model.comments}
                                onChange={handleChange}
                                placeholder={t("Product.ProductRequest.Comments")}
                            />
                        </div>
                        <div>
                            {isLoading ? 
                                <LoadingButton />
                                :
                                <button className="btn btn-custom flex flex-row justify-center items-center gap-2" onClick={handleRequest}>{t("Product.ProductRequest.BtnSendRequest")}<SendHorizonal></SendHorizonal></button>
                            }
                        </div>
                    </div>
                </div>
                <div className="opacity-50 fixed inset-0 z-[46] bg-black"></div>
            </div>
        </React.Fragment>
    );
}

export default withNotifications(withTranslation(ProductRequest));